import { observer } from 'mobx-react-lite'
import { useGapDataGridStore } from '../GapDataGridStore'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import TextField from '@mui/material/TextField'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import useInitialMount from 'reusable/hooks/useInitialMount'
import InputAdornment from '@mui/material/InputAdornment'
import Search from '@mui/icons-material/Search'
import Clear from '@mui/icons-material/Clear'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import useStore from 'reusable/mobx-store-utils'
import GapDataGridToolbarStore, { GapDataGridToolbarStoreProvider, useGapDataGridToolbarStore } from './GapDataGridToolbarStore'
import Button from '@mui/material/Button'
import FilterListOutlined from '@mui/icons-material/FilterListOutlined'
import Chip from '@mui/material/Chip'

import { GapDataGridAdditionalFilter } from '../GapDataGrid'
import Check from '@mui/icons-material/Check'
import { GridValidRowModel } from '@mui/x-data-grid'

export default observer(function GapDataGridToolbar<T extends GridValidRowModel>() {
   const gapDataGridStore = useGapDataGridStore()

   const gapDataGridToolbarStore = useStore(sp => new GapDataGridToolbarStore<T>(sp), {
      gapDataGridStore
   })

   useInitialMount(() => {
      if (gapDataGridStore.sp.toolbar?.additionalFilters) {
         gapDataGridToolbarStore.selectedFilter = gapDataGridToolbarStore.defaultAdditionalFilter
      }
      gapDataGridToolbarStore.setFilterModel()
   })

   return <Toolbar sx={{ px: '0 !important', minHeight: '0 !important', flexDirection: 'column', alignItems: 'stretch' }}>
      <GapDataGridToolbarStoreProvider store={gapDataGridToolbarStore}>
         <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {gapDataGridStore.sp.toolbar?.startAdornment}
            {gapDataGridStore.tabs && <Tabs value={gapDataGridToolbarStore.tab} onChange={gapDataGridToolbarStore.changeTabs} >
               {gapDataGridStore.tabs.map((t, i) => <Tab key={t.label} value={i} label={t.label} sx={{ textTransform: "none", minWidth: '10px', p: 1 }} />)}
            </Tabs>}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
               {gapDataGridToolbarStore.selectionButton && <Button onClick={gapDataGridToolbarStore.selectionButton.onClick} sx={{ mr: 2 }}>
                  {gapDataGridToolbarStore.selectionButton.label}
               </Button>}

               <TextField
                  placeholder="Search"
                  size="small"
                  onChange={gapDataGridToolbarStore.changeSearch}
                  value={gapDataGridToolbarStore.search}
                  slotProps={{
                     input: {
                        startAdornment: (
                           <InputAdornment position="start">
                              <Search sx={{ color: 'text.secondary' }} />
                           </InputAdornment>
                        ),
                        endAdornment: gapDataGridToolbarStore.search !== '' && <InputAdornment position="end">
                           <IconButton
                              id='clear-search'
                              onClick={gapDataGridToolbarStore.clearSearch}
                              size="small">
                              <Clear />
                           </IconButton>
                        </InputAdornment>
                     },
                  }}
                  sx={{
                     width: 200,
                     maxWidth: '100%',
                  }}
               />
            </Box>
            {gapDataGridStore.sp.toolbar?.additionalFilters && <IconButton onClick={gapDataGridToolbarStore.filterState.toggle}>
               <FilterListOutlined />
            </IconButton>}
            {gapDataGridStore.sp.toolbar?.archive && <Tooltip title={gapDataGridStore.archiveMode ? 'View active' : 'View archive'}>
               <IconButton
                  onClick={gapDataGridStore.toggleArchiveMode}
                  sx={{
                     ml: 1,
                     bgcolor: gapDataGridStore.archiveMode ? 'action.selected' : 'transparent',
                  }}
               >
                  <Inventory2Outlined />
               </IconButton>
            </Tooltip>}
         </Box>
         {gapDataGridStore.sp.toolbar?.additionalFilters && <Box
            sx={{
               width: '100%',
               mt: gapDataGridToolbarStore.filterState.visible ? 2 : 0,
               mb: gapDataGridToolbarStore.filterState.visible ? 1 : 0,
               overflow: 'hidden',
               transition: 'all 0.3s ease-in-out',
               maxHeight: gapDataGridToolbarStore.filterState.visible ? '100px' : '0px',
               opacity: gapDataGridToolbarStore.filterState.visible ? 1 : 0,
               transform: gapDataGridToolbarStore.filterState.visible ? 'translateY(0)' : 'translateY(-10px)'
            }}
         >
            {gapDataGridStore.sp.toolbar.additionalFilters.map(f => <GapDataGridToolbarFilterChip<T> key={f.name} filter={f} />)}
         </Box>}
      </GapDataGridToolbarStoreProvider>
   </Toolbar>
})

const GapDataGridToolbarFilterChip = observer(function GapDataGridToolbarFilterChip<T>(props: { filter: GapDataGridAdditionalFilter<T> }) {
   const gapDataGridToolbarStore = useGapDataGridToolbarStore()
   const gapDataGridToolbarFilterChipStore = useStore(sp => ({
      get isSelected() {
         return props.filter.name === sp.gapDataGridToolbarStore.selectedFilter?.name
      },
      selectFilter: () => {
         if (gapDataGridToolbarFilterChipStore.isSelected) {
            sp.gapDataGridToolbarStore.selectedFilter = gapDataGridToolbarStore.defaultAdditionalFilter
         } else {
            sp.gapDataGridToolbarStore.selectedFilter = props.filter
         }
         props.filter.onClick()
      }
   }), { gapDataGridToolbarStore })

   return <Chip
      label={props.filter.name}
      key={props.filter.name}
      size="small"
      icon={gapDataGridToolbarFilterChipStore.isSelected ? <Check /> : undefined}
      sx={{
         backgroundColor: gapDataGridToolbarFilterChipStore.isSelected ? 'action.selected' : 'transparent',
         border: 1,
         borderColor: 'divider',
         color: 'text.primary',
         mr: 1,
         cursor: 'pointer'
      }}
      onClick={gapDataGridToolbarFilterChipStore.selectFilter}
   />
})
